import { Link, Navigate } from 'react-router-dom';
import { PlaneLanding } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useArrivalInformation } from '../hooks/useArrivalInformation';
import type { ArrivalInformation } from '../lib/portal';

/**
 * Full arrival information page (Task #1673). Route: /arrival-information
 * (registered inside the authenticated shell — AppLayout provides the page
 * chrome, so this component renders content only, no viewport wrapper).
 *
 * Renders the complete placement details for a post-placement applicant.
 * When `available: false` (pre-placement) it declaratively redirects to
 * /dashboard via <Navigate> — the page is never reachable without a
 * confirmed placement, matching the card's absence on the dashboard.
 *
 * Shares its query (key + staleTime) with ArrivalInformationCard through
 * useArrivalInformation(), so arriving from the dashboard renders instantly
 * from cache.
 */

/** ISO weekday number (1 = Monday … 7 = Sunday) to short label; numbers
 *  outside the contract range render as-is rather than crashing. */
const weekday = (d: number) =>
  ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][d - 1] ?? String(d);

export default function ArrivalInformationPage() {
  const { data, isLoading, isError } = useQuery(useArrivalInformation());

  if (isLoading) {
    return <div className="card skeleton">Loading arrival information…</div>;
  }

  if (isError || !data) {
    return (
      <div className="card" role="alert">
        <p style={{ margin: 0 }}>Unable to load arrival information. Please try again later.</p>
      </div>
    );
  }

  // Pre-placement: declarative redirect (never call navigate() during render).
  if (!data.available) return <Navigate to="/dashboard" replace />;

  const info: ArrivalInformation = data;

  return (
    <>
      <header className="pagehead">
        <div>
          <h1>
            <PlaneLanding size={20} aria-hidden /> Arrival information
          </h1>
          {info.hostName && (
            <p style={{ margin: 0, color: 'var(--ink-2)' }}>
              Your confirmed placement details for {info.hostName}.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Link to="/travel" className="btn-outline">Local travel options</Link>
          <Link to="/dashboard" className="btn-outline">Back to dashboard</Link>
        </div>
      </header>

      <div className="card" data-testid="arrival-placement">
        <h2 style={{ marginTop: 0 }}>Placement</h2>
        <ul className="feed">
          {info.hostName && <li><strong>Organisation:</strong> {info.hostName}</li>}
          {info.returnToUponArrival && <li><strong>Report to upon arrival:</strong> {info.returnToUponArrival}</li>}
          {info.reportingTo && <li><strong>Reporting to:</strong> {info.reportingTo}</li>}
          {info.portOfEntryAirport && <li><strong>Port of entry airport:</strong> {info.portOfEntryAirport}</li>}
          {info.nearestAirportToJobsite && <li><strong>Nearest airport to jobsite:</strong> {info.nearestAirportToJobsite}</li>}
          {info.preferredArrivalDays.length > 0 && (
            <li><strong>Preferred arrival days:</strong> {info.preferredArrivalDays.map(weekday).join(', ')}</li>
          )}
          {info.preferredArrivalTimes && <li><strong>Preferred arrival times:</strong> {info.preferredArrivalTimes}</li>}
        </ul>
      </div>

      <div className="card" data-testid="arrival-contact">
        <h2 style={{ marginTop: 0 }}>Contact</h2>
        {info.contactName || info.contactTitle || info.contactEmail || info.contactPhoneNumber ? (
          <ul className="feed">
            {info.contactName && <li><strong>Name:</strong> {info.contactName}</li>}
            {info.contactTitle && <li><strong>Title:</strong> {info.contactTitle}</li>}
            {info.contactEmail && <li><strong>Email:</strong> {info.contactEmail}</li>}
            {/* contactPhoneNumber is only non-null when shouldContactBeforeArrival
                is true (contract rule) — re-gated here for defence in depth. */}
            {info.shouldContactBeforeArrival && info.contactPhoneNumber && (
              <li><strong>Phone (contact before arrival):</strong> {info.contactPhoneNumber}</li>
            )}
          </ul>
        ) : (
          <p className="muted" style={{ margin: 0 }}>No arrival contact has been provided yet.</p>
        )}
      </div>

      <div className="card" data-testid="arrival-transport">
        <h2 style={{ marginTop: 0 }}>Transport</h2>
        {info.pickUpProvided ? (
          <ul className="feed">
            <li><strong>Pick-up provided:</strong> Yes</li>
            {/* transportPickUpCost is only non-null when pickUpProvided is true
                (contract rule); absent cost with pick-up means it's included. */}
            <li><strong>Pick-up cost:</strong> {info.transportPickUpCost ?? 'Included'}</li>
          </ul>
        ) : (
          <p className="muted" style={{ margin: 0 }}>No transport pick-up is provided for this placement.</p>
        )}
      </div>

      {info.generalArrivalInstructions && (
        <div className="card" data-testid="arrival-instructions">
          <h2 style={{ marginTop: 0 }}>Additional notes</h2>
          <p className="globus-msg" style={{ margin: 0 }}>{info.generalArrivalInstructions}</p>
        </div>
      )}
    </>
  );
}
